import { useQuery, useMutation } from "@tanstack/react-query";
import { privateHttp } from "~/config/axios";
import { API_ENDPOINTS } from "~/config/endpoints";
import {
  BusinessCompanyPhotoListResponse,
  BusinessCompanyPhotoDetailRequest,
  BusinessCompanyPhotoDetailResponse,
  BusinessCompanyPhotoCreateRequest,
  BusinessCompanyPhotoCreateResponse,
  BusinessCompanyPhotoDeleteRequest,
  BusinessCompanyPhotoDeleteResponse,
} from "~/types/common";

// Business Company Photo 목록 조회
const getBusinessCompanyPhotoList =
  async (): Promise<BusinessCompanyPhotoListResponse> => {
    try {
      const { data } = await privateHttp.get(
        API_ENDPOINTS.BUSINESS_COMPANY_PHOTO.LIST
      );
      return data;
    } catch (error) {
      console.error("사업장 사진 목록 API 호출 에러:", error);
      throw error;
    }
  };

export const useBusinessCompanyPhotoList = () => {
  return useQuery({
    queryKey: ["businessCompanyPhotoList"],
    queryFn: getBusinessCompanyPhotoList,
    staleTime: 5 * 60 * 1000, // 5분
    gcTime: 10 * 60 * 1000, // 10분
  });
};

// Business Company Photo 상세 조회
const getBusinessCompanyPhotoDetail = async (
  params: BusinessCompanyPhotoDetailRequest
): Promise<BusinessCompanyPhotoDetailResponse> => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.BUSINESS_COMPANY_PHOTO.DETAIL,
      {
        params,
      }
    );
    return data;
  } catch (error) {
    console.error("사업장 사진 상세 API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessCompanyPhotoDetail = () => {
  return useMutation({
    mutationFn: getBusinessCompanyPhotoDetail,
  });
};

// Business Company Photo 생성
const createBusinessCompanyPhoto = async (
  requestData: BusinessCompanyPhotoCreateRequest
): Promise<BusinessCompanyPhotoCreateResponse> => {
  try {
    const formData = new FormData();

    // 파일들을 FormData에 추가
    requestData.files.forEach((file) => {
      formData.append("files", file);
    });

    const { data } = await privateHttp.post(
      API_ENDPOINTS.BUSINESS_COMPANY_PHOTO.CREATE,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return data;
  } catch (error) {
    console.error("사업장 사진 생성 API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessCompanyPhotoCreate = () => {
  return useMutation({
    mutationFn: createBusinessCompanyPhoto,
  });
};

// Business Company Photo 삭제
const deleteBusinessCompanyPhoto = async (
  params: BusinessCompanyPhotoDeleteRequest
): Promise<BusinessCompanyPhotoDeleteResponse> => {
  try {
    const { data } = await privateHttp.delete(
      `${API_ENDPOINTS.BUSINESS_COMPANY_PHOTO.DELETE}?companyPhotoSeq=${params.companyPhotoSeq}`
    );
    return data;
  } catch (error) {
    console.error("사업장 사진 삭제 API 호출 에러:", error);
    throw error;
  }
};

export const useBusinessCompanyPhotoDelete = () => {
  return useMutation({
    mutationFn: deleteBusinessCompanyPhoto,
  });
};

// 대표 사진 지정 (매장당 1개)
const setRepresentativeCompanyPhoto = async (companyPhotoSeq: number) => {
  const { data } = await privateHttp.put(
    `${API_ENDPOINTS.BUSINESS_COMPANY_PHOTO.REPRESENTATIVE}?companyPhotoSeq=${companyPhotoSeq}`
  );
  return data;
};

export const useBusinessCompanyPhotoSetRepresentative = () => {
  return useMutation({
    mutationFn: setRepresentativeCompanyPhoto,
  });
};

// 사진 순서 저장 (companyPhotoSeq 배열 순서)
const updateCompanyPhotoOrder = async (orderedSeqs: number[]) => {
  const { data } = await privateHttp.put(
    API_ENDPOINTS.BUSINESS_COMPANY_PHOTO.ORDER,
    orderedSeqs
  );
  return data;
};

export const useBusinessCompanyPhotoOrder = () => {
  return useMutation({
    mutationFn: updateCompanyPhotoOrder,
  });
};

// 매장 미디어(영상 등) 등록 — presigned/브릿지로 올린 fileSeq 를 매장 미디어로 등록
const saveCompanyMedia = async (params: {
  fileSeq: number;
  cdMediaType: "IMAGE" | "VIDEO";
}) => {
  const { data } = await privateHttp.post(
    `${API_ENDPOINTS.BUSINESS_COMPANY_PHOTO.MEDIA}?fileSeq=${params.fileSeq}&cdMediaType=${params.cdMediaType}`
  );
  return data;
};

export const useBusinessCompanyPhotoSaveMedia = () => {
  return useMutation({
    mutationFn: saveCompanyMedia,
  });
};
